/**
 * Inputs: place name / maps URL / address fields. Outputs: filled driver-card address from a pasted Google Maps link.
 * Paste the share link first. Name and address fill when the link can be read. Owner can still edit.
 */

import { useEffect, useRef, type ChangeEvent } from "react";
import { isAllowedMapsUrl, parseMapsUrl } from "@shared/maps-url.ts";
import { applyMapsHintToDraft, type PlaceDraftFields } from "@shared/maps-place.ts";
import { readMapsPlace } from "../../lib/api.ts";

export function PlaceLinkFields({
  value,
  onChange,
  showName = true,
  nameLabel = "Place name",
  mapLabel = "Google Maps link",
}: {
  value: PlaceDraftFields;
  onChange: (next: PlaceDraftFields) => void;
  showName?: boolean;
  nameLabel?: string;
  mapLabel?: string;
}) {
  const mapsInputRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    mapsInputRef.current?.scrollIntoView({ block: "center", behavior: reduce ? "auto" : "smooth" });
  }, []);

  useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  useEffect(() => {
    const url = value.mapsUrl.trim();
    if (!url) {
      if (valueRef.current.lookupStatus !== "idle" || valueRef.current.lookupNote) {
        onChangeRef.current({ ...valueRef.current, lookupStatus: "idle", lookupNote: null });
      }
      return;
    }
    const parsed = parseMapsUrl(url);
    if (!parsed.ok) {
      onChangeRef.current({
        ...valueRef.current,
        lookupStatus: "invalid",
        lookupNote: parsed.error,
      });
      return;
    }
    const current = valueRef.current;
    const local = applyMapsHintToDraft(current, {
      mapsUrl: url,
      name: parsed.name,
      address: parsed.address,
      thaiName: null,
      coordinates: parsed.coordinates,
      addressSource: parsed.address ? "maps_url" : null,
      note: parsed.address
        ? "Address taken from the map link. Check it, then it can go on the driver card."
        : parsed.isShortLink
          ? "Reading the short map link…"
          : "Looking up a street address for the driver card…",
    });
    const hadAddress = Boolean(current.address.trim());
    onChangeRef.current({
      ...local,
      lookupStatus: parsed.address || hadAddress ? "ready" : "reading",
      lookupNote: hadAddress && !parsed.address ? null : local.lookupNote,
    });
    if (hadAddress) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      void readMapsPlace(url, controller.signal)
        .then((data) => {
          if (controller.signal.aborted) return;
          onChangeRef.current(applyMapsHintToDraft(valueRef.current, data.hint));
        })
        .catch((error: unknown) => {
          if (controller.signal.aborted) return;
          const latest = valueRef.current;
          const message = error instanceof Error ? error.message : "Could not read that map link.";
          onChangeRef.current({
            ...latest,
            lookupStatus: latest.address.trim() ? "ready" : "partial",
            lookupNote: latest.address.trim()
              ? latest.lookupNote
              : `${message} Add the street address below so the driver card can show it.`,
          });
        });
    }, 350);
    return () => {
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [value.mapsUrl]);

  function set<K extends keyof PlaceDraftFields>(key: K, next: PlaceDraftFields[K]) {
    onChange({ ...value, [key]: next });
  }

  function onNameChange(event: ChangeEvent<HTMLInputElement>) {
    const next = event.target.value;
    if (isAllowedMapsUrl(next) && !value.mapsUrl.trim()) {
      onChange({ ...value, mapsUrl: next.trim(), name: "" });
      return;
    }
    set("name", next);
  }

  const previewName = value.name.trim();
  const previewAddress = value.address.trim();

  return (
    <div className="place-link-fields">
      <label className="field">
        {mapLabel}
        <input
          ref={mapsInputRef}
          value={value.mapsUrl}
          onChange={(e) => set("mapsUrl", e.target.value)}
          inputMode="url"
          autoComplete="off"
          placeholder="https://maps.app.goo.gl/…"
        />
      </label>
      <p className="muted">Paste the Google Maps share link. We fill the name and address so the driver card can show them.</p>
      {showName ? (
        <label className="field">
          {nameLabel}
          <input value={value.name} onChange={onNameChange} />
        </label>
      ) : null}
      <label className="field">
        Address for the driver
        <textarea
          value={value.address}
          onChange={(e) => set("address", e.target.value)}
          rows={3}
          placeholder="Street, soi, district"
        />
      </label>
      <label className="field">
        Thai name (optional)
        <input value={value.thaiName} onChange={(e) => set("thaiName", e.target.value)} lang="th" />
      </label>
      {value.lookupStatus === "reading" ? <p className="muted">{value.lookupNote ?? "Reading the map link…"}</p> : null}
      {value.lookupStatus === "invalid" ? <p className="banner warn">{value.lookupNote}</p> : null}
      {value.lookupStatus === "partial" && value.lookupNote ? <p className="muted">{value.lookupNote}</p> : null}
      {previewAddress ? (
        <aside className="driver-preview" aria-label="Driver card preview">
          <p className="kicker">Driver card</p>
          {previewName ? <p className="serif driver-preview-name">{previewName}</p> : null}
          {value.thaiName.trim() ? <p lang="th">{value.thaiName.trim()}</p> : null}
          <p>{previewAddress}</p>
        </aside>
      ) : null}
    </div>
  );
}
